import React, { FC, useEffect, useState } from 'react';
import { Button, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { FetchSynonymsOptions, Synonym } from '@packages/models/api/synonyms-search-term';
import { Form, Modal } from 'react-bootstrap';
import { StyleSheet } from 'react-native';
import { synonymsService } from '@web/services/singletons';
import { TagInput } from '@web/components/tag-input';
import { useSynonymsDispatch, updateSynonym, useSynonymsState } from '@packages/contexts/synonyms';
import { useTranslation } from 'react-i18next';

interface EditSynonymModalProps {
    handleSortChange: (options: FetchSynonymsOptions) => void;
    onHide: () => void;
    selectedSynonym?: Synonym;
    show: boolean;
    showToastMessage: (message: string, type: 'success' | 'error') => void;
}

const useStyles = createUseStyles({
    header: {
        padding: '16px 24px!important',
    },
    title: {
        fontSize: '21px!important',
        fontWeight: '600!important',
    },
    body: {
        padding: '16px 24px!important',
    },
    bodyDescription: {
        fontSize: '15px!important',
        fontWeight: '400!important',
    },
    footer: {
        padding: '24px!important',
    },
    formLabel: {
        fontSize: '15px',
    },
});

const styles = StyleSheet.create({
    bodyDescription: {
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 24,
    },
});

export const EditSynonymModal: FC<EditSynonymModalProps> = ({
    show,
    onHide,
    selectedSynonym,
    showToastMessage,
    handleSortChange,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [synonyms, setSynonyms] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isChangeForm, setIsChangeForm] = useState(false);
    const synonymDispatch = useSynonymsDispatch();
    const { error } = useSynonymsState();

    const closeModal = () => {
        onHide();
        setSynonyms([]);
        setSearchTerm('');
        setIsChangeForm(false);
    };

    const handleEditSynonym = (synonym: string) => {
        if (synonym.trim() && !synonyms.includes(synonym.trim())) {
            setSynonyms((prev) => {
                const newSynonyms = [...prev, synonym.trim()];
                setIsChangeForm(true);
                return newSynonyms;
            });
        }
    };

    const handleRemoveSynonym = (index: number) => {
        setSynonyms((prev) => {
            const newSynonyms = prev.filter((_, i) => i !== index);
            setIsChangeForm(true);
            return newSynonyms;
        });
    };

    const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
        setIsChangeForm(
            value !== selectedSynonym?.searchTerm || !areArraysEqual(synonyms, selectedSynonym?.synonyms || [])
        );
    };

    const areArraysEqual = (arr1: string[], arr2: string[]): boolean => {
        return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
    };

    async function handleUpdateSynonym() {
        const updatedRequest = {
            searchTerm: searchTerm !== selectedSynonym?.searchTerm ? searchTerm : undefined,
            synonyms,
        };

        try {
            if (selectedSynonym?.searchTermId) {
                await updateSynonym(synonymDispatch, synonymsService, {
                    id: selectedSynonym.searchTermId,
                    ...updatedRequest,
                });
            }
            if (error) {
                showToastMessage(t('Failed to update synonym'), 'error');
            }
            handleSortChange({ sort: 'updatedAt', direction: 'desc' });
        } catch (error) {
            showToastMessage(t('Failed to update synonym'), 'error');
        } finally {
            closeModal();
        }
    }

    useEffect(() => {
        if (selectedSynonym) {
            setSynonyms(selectedSynonym.synonyms || []);
            setSearchTerm(selectedSynonym.searchTerm || '');
        }
        setIsChangeForm(false);
    }, [selectedSynonym]);

    return (
        <Modal show={show} centered onHide={closeModal} size="lg">
            <Modal.Header className={classes.header}>
                <Modal.Title className={classes.title}>{t('Edit Synonyms')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className={classes.body}>
                <Typography variant="caption" color="black" style={styles.bodyDescription}>
                    {t(
                        "Enter the search term and add all related words as synonyms to enhance the search engine's effectiveness."
                    )}
                </Typography>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Form.Group controlId="search-term" className="mb-3 mt-2">
                        <Form.Label className={classes.formLabel}>{t('Search Term')}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('Enter search term')}
                            maxLength={50}
                            required
                            value={searchTerm}
                            onChange={handleSearchTermChange}
                        />
                    </Form.Group>

                    <Form.Group controlId="synonyms" className="mb-3">
                        <Form.Label className={classes.formLabel}>{t('Synonyms')}</Form.Label>
                        <TagInput
                            placeholder="Enter Synonyms"
                            tags={synonyms}
                            handleAdd={handleEditSynonym}
                            handleRemove={handleRemoveSynonym}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className={classes.footer}>
                <Button variant="ghost-blue" style={{ marginRight: 8 }} onPress={closeModal}>
                    {t('Cancel')}
                </Button>
                <Button variant="primary" onPress={handleUpdateSynonym} disabled={!isChangeForm}>
                    {t('Save changes')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
